import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { SelectPanelWidthDirective } from '../../_helpers/select-panel-width.directive';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

import { SampleFilterMode } from 'src/app/enums/sampleFilterMode.enum';
import { SampleFilterDimension } from 'src/app/models/risk-assessment.model';
import { SearchableList } from 'src/app/components/dashboard/dashboard-widgets/_shared/searchable-list';
import { MultiSelectClearComponent } from 'src/app/components/dashboard/dashboard-widgets/_shared/multi-select-clear/multi-select-clear.component';
import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { generateGUID } from 'src/app/_helpers/guid.helper';

/** A single selectable value for a dimension. `id` is a numeric enum value or a GUID string. */
export interface SampleFilterOption {
    id: number | string;
    name: string;
}

/**
 * Sentinel bound in the picker to represent the first-class (None) / unspecified bucket. It never
 * lands in the emitted `values` array — it maps to the dimension's `noneSelected` flag instead. The
 * value is namespaced so it cannot collide with a real enum int or GUID option id.
 */
export const SAMPLE_FILTER_NONE = '__sample_filter_none__';

interface ModeOption {
    mode: SampleFilterMode;
    label: string;
}

/**
 * Reusable three-mode dimension control for the G19 RA sample filters. Presents a segmented radio —
 * All / All except {…} / Only these {…} — and reveals a searchable multi-select picker only in the
 * two non-All modes. Selected-but-search-filtered-out values stay visible, and `All` renders
 * chip-free as an affirmative statement rather than an empty picker.
 *
 * Operates on the uniform {@link SampleFilterDimension} view model so all three RA dimensions (shift
 * coverage, sample type, survey type) can consume it: the shift-coverage/sample-type dimensions carry
 * numeric enum ids, the survey-type dimension carries GUID ids. Emits plain objects/arrays only (no
 * Set/Map), so it round-trips through the RA form's `JSON.parse(JSON.stringify(entity))` deep clone.
 *
 * NOTE — legal-but-unsaveable intermediate states: switching to `AllExcept`/`OnlyThese` (or clearing the
 * picker) emits a value with an empty selection, e.g. `{ mode: OnlyThese, values: [], noneSelected:
 * false }`. This is a valid *transient* editing state, but the BE (`RiskAssessmentSampleFilterHelper`)
 * rejects two shapes:
 *  - `SelectsNothing`   — any non-`All` mode with zero selections (mode != All && selectedCount == 0);
 *  - `ExcludesEverything` — `AllExcept` with every option selected (selectedCount >= totalOptions),
 *    where the BE counts `Enum.GetValues(T).Length + (hasNoneOption ? 1 : 0)`.
 * Both produce a 400. Consumers MUST gate save on the {@link validChange} output (which is `false` for
 * either shape) and not persist while it is `false`.
 *
 * CONTRACT for FE-2 — the `ExcludesEverything` check counts `options.length` on the FE against
 * `Enum.GetValues(T).Length` on the BE, so for the two enum dimensions FE-2 MUST pass the *full* option
 * set. A curated subset would flag validity `false` early (a safe false-disable, never a false 400). For
 * the GUID-keyed survey-type dimension the BE rule is over visible options, so `options.length` matches
 * exactly.
 */
@Component({
    standalone: true,
    selector: 'app-sample-filter-dimension',
    imports: [
    FormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    MultiSelectClearComponent,
    SelectPanelWidthDirective
],
    templateUrl: './sample-filter-dimension.component.html',
    styleUrls: ['./sample-filter-dimension.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SampleFilterDimensionComponent implements OnChanges {
    /** Optional heading rendered above the control. */
    @Input() label = '';
    /** Affirmative label for the All mode, e.g. "All shift types". Falls back to "All". */
    @Input() allLabel = '';
    /** The full set of selectable values for this dimension. */
    @Input() options: SampleFilterOption[] = [];
    /** Whether the first-class (None) / unspecified bucket is offered (nullable dimensions only). */
    @Input() includeNone = false;
    /** Translate key for the (None) option. */
    @Input() noneLabel = 'None';
    /** Translate key for the picker placeholder. */
    @Input() pickerPlaceholder = 'Select values';
    /** Read-only presentation (e.g. on Approved assessments). */
    @Input() disabled = false;
    /** Current dimension value. `null`/`undefined` is treated as All. */
    @Input() value: SampleFilterDimension<number | string> | null = null;
    @Output() valueChange = new EventEmitter<SampleFilterDimension<number | string>>();
    /**
     * Emits the current save-validity: `false` while the value is a BE-invalid shape — a non-`All` mode
     * with an empty selection ({@link selectsNothing}) OR `AllExcept` with every option selected
     * ({@link excludesEverything}) — `true` otherwise. Fires alongside every {@link valueChange} on user
     * interaction AND on parent-driven `value` changes / init (deferred to the next microtask, and only
     * when the emitted validity actually changes). Consumers should disable save while this is `false`.
     */
    @Output() validChange = new EventEmitter<boolean>();

    readonly SampleFilterMode = SampleFilterMode;
    readonly noneSentinel = SAMPLE_FILTER_NONE;
    readonly trackById = trackById;
    readonly trackByIndex = trackByIndex;
    readonly radioName = `sample-filter-mode-${generateGUID()}`;

    mode: SampleFilterMode = SampleFilterMode.All;
    pickerVisible = false;
    hasSelection = false;
    /**
     * True when the current value is a non-`All` mode with nothing selected — the BE-invalid
     * `SelectsNothing` state that must not be persisted. Derived in {@link rebuildDerivedState}.
     */
    selectsNothing = false;
    /**
     * True when the current value is `AllExcept` with every option (incl. the (None) bucket when
     * offered) selected — the BE-invalid `ExcludesEverything` state that must not be persisted. Derived
     * in {@link rebuildDerivedState}. See the class-level FE-2 contract note re: passing the full option
     * set.
     */
    excludesEverything = false;
    modeOptions: ModeOption[] = [];
    selectValue: (number | string)[] = [];

    readonly optionList = new SearchableList<SampleFilterOption>(
        (option, query) => this.translate.instant(option.name).toLowerCase().includes(query),
        () => this.selectedIds,
    );

    private selectedIds: (number | string)[] = [];
    private noneSelected = false;
    /** Last value emitted on {@link validChange}; gates re-emission so we only fire on real changes. */
    private lastEmittedValid: boolean | null = null;

    constructor(private readonly translate: TranslateService) {
        this.buildModeOptions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['allLabel']) {
            this.buildModeOptions();
        }
        if (changes['options']) {
            this.optionList.setItems(this.options ?? []);
        }
        // includeNone going false must drop a now-unofferable (None), mirroring syncFromValue's guard.
        if (changes['includeNone'] && !this.includeNone) {
            this.noneSelected = false;
        }

        if (changes['value']) {
            this.syncFromValue();
        } else if (changes['options'] || changes['includeNone']) {
            // excludesEverything is derived from options.length / includeNone, so an options/includeNone
            // change that does not coincide with a value change must recompute and re-emit validity —
            // otherwise validChange goes stale in the unsafe direction (e.g. survey-type options load
            // async after [value] binds). Do NOT call syncFromValue() here: it re-reads this.value and
            // would clobber uncommitted selections under one-way [value] + (valueChange).
            this.optionList.syncSelection();
            this.rebuildDerivedState();
            Promise.resolve().then(() => this.emitValidity());
        }
    }

    onModeChange(mode: SampleFilterMode): void {
        this.mode = mode;
        this.pickerVisible = mode !== SampleFilterMode.All;
        if (mode === SampleFilterMode.All) {
            this.selectedIds = [];
            this.noneSelected = false;
        }
        this.afterStateChange();
    }

    onSelectionChange(values: (number | string)[]): void {
        const next = values ?? [];
        this.noneSelected = this.includeNone && next.includes(this.noneSentinel);
        this.selectedIds = next.filter(value => value !== this.noneSentinel);
        this.afterStateChange();
    }

    onSearch(query: string): void {
        this.optionList.search(query ?? '');
    }

    clearSelection(): void {
        this.selectedIds = [];
        this.noneSelected = false;
        this.afterStateChange();
    }

    private afterStateChange(): void {
        this.optionList.syncSelection();
        this.rebuildDerivedState();
        this.emit();
    }

    private syncFromValue(): void {
        this.mode = this.value?.mode ?? SampleFilterMode.All;
        this.selectedIds = [...(this.value?.values ?? [])];
        // Guard noneSelected by includeNone so a hand-built parent default cannot make hasSelection true
        // for a dimension that does not offer (None) — the BE ignores NoneSelected when hasNoneOption is
        // false and would still return SelectsNothing.
        this.noneSelected = this.includeNone && (this.value?.noneSelected ?? false);
        this.pickerVisible = this.mode !== SampleFilterMode.All;
        this.optionList.syncSelection();
        this.rebuildDerivedState();
        // Re-emit validity on parent-driven value changes / init so the consumer's flag never goes stale
        // (e.g. a Cancel/reset restoring a valid dimension after the picker was cleared). Deferred to the
        // next microtask so emitting an @Output during the parent's CD pass cannot throw
        // ExpressionChangedAfterItHasBeenChecked; change-gated inside emitValidity().
        Promise.resolve().then(() => this.emitValidity());
    }

    private rebuildDerivedState(): void {
        this.selectValue =
            this.includeNone && this.noneSelected
                ? [...this.selectedIds, this.noneSentinel]
                : [...this.selectedIds];
        this.hasSelection = this.selectedIds.length > 0 || this.noneSelected;
        this.selectsNothing = this.mode !== SampleFilterMode.All && !this.hasSelection;
        // ExcludesEverything mirrors RiskAssessmentSampleFilterHelper: AllExcept with every option
        // (incl. the (None) bucket when offered) selected. selectedCount / totalOptions both count the
        // (None) bucket when includeNone is true.
        const selectedCount = this.selectedIds.length + (this.includeNone && this.noneSelected ? 1 : 0);
        const totalOptions = (this.options?.length ?? 0) + (this.includeNone ? 1 : 0);
        this.excludesEverything =
            this.mode === SampleFilterMode.AllExcept &&
            totalOptions > 0 &&
            selectedCount >= totalOptions;
    }

    private buildModeOptions(): void {
        this.modeOptions = [
            { mode: SampleFilterMode.All, label: this.allLabel || 'All' },
            { mode: SampleFilterMode.AllExcept, label: 'All except' },
            { mode: SampleFilterMode.OnlyThese, label: 'Only these' },
        ];
    }

    private emit(): void {
        this.valueChange.emit({
            mode: this.mode,
            values: [...this.selectedIds],
            noneSelected: this.noneSelected,
        });
        this.emitValidity();
    }

    /** Emits {@link validChange} only when the computed save-validity actually changes. */
    private emitValidity(): void {
        const valid = !this.selectsNothing && !this.excludesEverything;
        if (valid === this.lastEmittedValid) {
            return;
        }
        this.lastEmittedValid = valid;
        this.validChange.emit(valid);
    }
}
